<script lang="ts">
    import * as t from '../../translations/News.ts';
    import Tabs from './Tabs.svelte';
    import type { BlogPost } from '../../api/blogs.ts';
    import type { Newsletter } from '../../api/news.ts';
    import { onMount } from 'svelte';
    import BlogCard from '../../lib/News/BlogCard.svelte';
    import NewsCard from '../../lib/News/NewsCard.svelte';
    import Pagination from '../../lib/Pagination.svelte';
    import SkeletonLoader from '../../lib/News/SkeletonLoader.svelte';
    import EmptyState from '../../lib/EmptyState.svelte';
    import { getBlogPosts } from '../../api/blogs.ts';
    import { getNewsletters } from '../../api/news.ts';
    import { vvSettings } from '../../api/common.ts';

    import news1 from '../../assets/news-placeholder-images/generated/news-placeholder-1_800.avif';
    import news2 from '../../assets/news-placeholder-images/generated/news-placeholder-2_800.avif';
    import news3 from '../../assets/news-placeholder-images/generated/news-placeholder-3_800.avif';
    import news4 from '../../assets/news-placeholder-images/generated/news-placeholder-4_800.avif';
    import news5 from '../../assets/news-placeholder-images/generated/news-placeholder-5_800.avif';
    import news6 from '../../assets/news-placeholder-images/generated/news-placeholder-6_800.avif';
    import news7 from '../../assets/news-placeholder-images/generated/news-placeholder-7_800.avif';
    import news8 from '../../assets/news-placeholder-images/generated/news-placeholder-8_800.avif';
    import news9 from '../../assets/news-placeholder-images/generated/news-placeholder-9_800.avif';
    import news10 from '../../assets/news-placeholder-images/generated/news-placeholder-10_800.avif';
    import news11 from '../../assets/news-placeholder-images/generated/news-placeholder-11_800.avif';
    import news12 from '../../assets/news-placeholder-images/generated/news-placeholder-12_800.avif';
    import news13 from '../../assets/news-placeholder-images/generated/news-placeholder-13_800.avif';
    import news14 from '../../assets/news-placeholder-images/generated/news-placeholder-14_800.avif';
    import news15 from '../../assets/news-placeholder-images/generated/news-placeholder-15_800.avif';
    import news16 from '../../assets/news-placeholder-images/generated/news-placeholder-16_800.avif';

    const TABS = {
        BLOGS: 'blogs',
        NEWS: 'news'
    };
    const newsPlaceholders = [news1, news2, news3, news4, news5, news6, news7, news8, news9, news10, news11, news12, news13, news14, news15, news16];

    const showBlogs = vvSettings?.showBlogs ?? true;
    const pageHeader = showBlogs ? t.Page_Header() : t.News_Tab();

    function getRandomImages(count: number, pool: string[]): string[] {
        const shuffled = [...pool].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, count);
    }

    let activeTab = TABS.BLOGS;
    let blogPosts: BlogPost[] = [];
    let newsletters: Newsletter[] = [];
    let groupedNewsletters: { [year: string]: Newsletter[] } = {};
    let selectedYear: string | null = null;
    let loading = false;
    let error = '';

    let currentPage = 1;
    const itemsPerPage = 10;

    // Define filteredNewsletters FIRST since other reactive statements depend on it
    $: filteredNewsletters = selectedYear ? groupedNewsletters[selectedYear] || [] : newsletters;
    
    // These reactive statements depend on the variables defined above
    $: visibleBlogPosts = blogPosts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    $: currentNewsImages = getRandomImages(filteredNewsletters.length < itemsPerPage ? filteredNewsletters.length : itemsPerPage, newsPlaceholders);
    $: visibleNewsletters = filteredNewsletters.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    function groupNewslettersByYear(newsletters: Newsletter[]) {
        return newsletters.reduce((acc, newsletter) => {
            const year = new Date(newsletter.date).getFullYear().toString();
            if (!acc[year]) {
                acc[year] = [];
            }
            acc[year].push(newsletter);
            return acc;
        }, {} as { [year: string]: Newsletter[] });
    }

    async function switchTab(tab: string) {
        activeTab = tab;
        sessionStorage.setItem('activeTab', tab);
        currentPage = 1;
        loading = true;
        error = '';

        try {
            if (tab === TABS.BLOGS) {
                blogPosts = await getBlogPosts();
            } else if (tab === TABS.NEWS) {
                newsletters = await getNewsletters();
                groupedNewsletters = groupNewslettersByYear(newsletters);

                // Reset selected year to latest when switching back to "News"
                selectedYear = Object.keys(groupedNewsletters).sort().reverse()[0];
            }
        } catch (err) {
            console.error(`Failed to fetch ${tab}:`, err);
            error = `Failed to load ${tab}.`;
        } finally {
            loading = false;
        }
    }

    function updateSelectedYear(year: string) {
        selectedYear = year;
        sessionStorage.setItem('selectedYear', year);
        currentPage = 1;
    }

    function setPage(page: number) {
        currentPage = page;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onMount(async () => {
        const savedTab = sessionStorage.getItem('activeTab');
        const savedYear = sessionStorage.getItem('selectedYear');

        loading = true;
        error = '';

        try {
            const [blogs, news] = await Promise.all([getBlogPosts(), getNewsletters()]);
            blogPosts = blogs;
            newsletters = news;

            groupedNewsletters = groupNewslettersByYear(newsletters);

            const shouldDefaultToNews = blogPosts.length === 0 && newsletters.length > 0;

            // If blogging is disabled, always show NEWS tab
            if (!showBlogs) {
                activeTab = TABS.NEWS;
            } else {
                activeTab = savedTab && (savedTab === TABS.BLOGS || savedTab === TABS.NEWS)
                    ? savedTab
                    : shouldDefaultToNews
                        ? TABS.NEWS
                        : TABS.BLOGS;
            }

            sessionStorage.setItem('activeTab', activeTab);

            if (activeTab === TABS.NEWS) {
                selectedYear = savedYear && groupedNewsletters[savedYear]
                    ? savedYear
                    : Object.keys(groupedNewsletters).sort().reverse()[0];
            }
        } catch (err) {
            console.error('Failed to fetch blog or news data:', err);
            error = 'Failed to load content.';
        } finally {
            loading = false;
        }
    });
</script>

<div class="@container w-full m-auto max-w-[1000px] py-4 px-4 md:px-8 flex flex-col gap-8 interfont">
    <div class="flex flex-col @xl:flex-row gap-4">
        <div class="flex-1">
            <h1 class="text-3xl md:text-4xl mt-1">{pageHeader}</h1>
        </div>
    </div>

    {#if showBlogs}
        <Tabs
                activeTab={activeTab}
                onSwitchTab={switchTab}
                labels={{
                blogs: t.Blogs_Tab(),
                news: t.News_Tab()
            }}
        />
    {/if}

    <div class="mt-6">
        {#if activeTab === 'news'}
            {#if loading}
                <SkeletonLoader type={activeTab} {itemsPerPage} />
            {:else if error}
                <p class="text-red-500" data-testid="error">{t.Error_Message()}</p>
            {:else if newsletters.length === 0}
                <EmptyState/>
            {:else}
                <!-- Archive Year Dropdown -->
                <div class="flex gap-4 items-center mb-6">
                    <label for="year-select" class="text-gray-700 font-medium">{t.Archive_Label()}</label>
                    <select
                            id="archive-year"
                            aria-label="Select a year to filter newsletters"
                            class="border border-gray-300 px-3 py-2 rounded-lg shadow-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-100 active:bg-gray-200 transition duration-200 ease-in-out"
                            bind:value={selectedYear}
                            on:change={() => selectedYear && updateSelectedYear(selectedYear)}
                    >
                        {#each Object.keys(groupedNewsletters).sort().reverse() as year}
                            <option value={year}>{year}</option>
                        {/each}
                    </select>
                </div>

                <ul class="flex flex-col gap-6 p-0 m-0 list-none" style="padding-left:0; margin-left:0; margin-block:0; list-style:none;" data-testid="news-list">
                    {#each visibleNewsletters as newsletter, index}
                        <li class="m-0 p-0 w-full" style="margin-left:0; padding-left:0; list-style:none;"><NewsCard {newsletter} image={currentNewsImages[index]} /></li>
                    {/each}
                </ul>
                <Pagination totalItems={filteredNewsletters.length} currentPage={currentPage} itemsPerPage={itemsPerPage} onPageChange={setPage} />
            {/if}
        {:else if activeTab === 'blogs'}
            {#if loading}
                <SkeletonLoader type={activeTab} {itemsPerPage} />
            {:else if error}
                <p class="text-red-500" data-testid="error">{t.Error_Message()}</p>
            {:else if blogPosts.length === 0}
                <EmptyState/>
            {:else}
                <ul class="flex flex-col gap-6 p-0 m-0 list-none" style="padding-left:0; margin-left:0; margin-block:0; list-style:none;" data-testid="blog-list">
                    {#each visibleBlogPosts as post}
                        <li class="m-0 p-0 w-full" style="margin-left:0; padding-left:0; list-style:none;"><BlogCard {post} /></li>
                    {/each}
                </ul>
                <Pagination totalItems={blogPosts.length} currentPage={currentPage} itemsPerPage={itemsPerPage} onPageChange={setPage} />
            {/if}
        {/if}
    </div>
</div>